/**
 * ConfidenceBadge
 *
 * Visual indicator for extraction confidence levels.
 */

import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type {
  ConfidenceScores,
  ConfidenceLevel,
} from '@/components/document-comparison/types';
import {
  getConfidenceLevel,
  getCombinedConfidence,
} from '@/components/document-comparison/types';

interface ConfidenceBadgeProps {
  /** Confidence scores from extraction */
  scores?: ConfidenceScores;
  /** Show percentage label */
  showLabel?: boolean;
  /** Size variant */
  size?: 'sm' | 'default';
  /** Additional CSS classes */
  className?: string;
}

const levelConfig: Record<
  ConfidenceLevel,
  {
    icon: typeof CheckCircle2;
    variant: 'success' | 'warning' | 'destructive' | 'secondary';
    label: string;
  }
> = {
  high: {
    icon: CheckCircle2,
    variant: 'success',
    label: 'High confidence',
  },
  medium: {
    icon: AlertTriangle,
    variant: 'warning',
    label: 'Medium confidence',
  },
  low: {
    icon: XCircle,
    variant: 'destructive',
    label: 'Low confidence',
  },
  unknown: {
    icon: HelpCircle,
    variant: 'secondary',
    label: 'No confidence data',
  },
};

export function ConfidenceBadge({
  scores,
  showLabel = false,
  size = 'default',
  className,
}: ConfidenceBadgeProps) {
  const level = getConfidenceLevel(scores);
  const combined = getCombinedConfidence(scores);
  const config = levelConfig[level];
  const Icon = config.icon;

  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const badgeSize = size === 'sm' ? 'text-[10px] px-1.5 py-0' : '';

  const tooltipContent = (
    <div className="space-y-1">
      <p className="font-medium">{config.label}</p>
      {scores && (
        <div className="text-xs text-muted-foreground space-y-0.5">
          {scores.ocrConfidence !== undefined &&
            scores.ocrConfidence !== null && (
              <p>OCR: {(scores.ocrConfidence * 100).toFixed(0)}%</p>
            )}
          {scores.logprobsConfidence !== undefined &&
            scores.logprobsConfidence !== null && (
              <p>Model: {(scores.logprobsConfidence * 100).toFixed(0)}%</p>
            )}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={config.variant}
          className={cn('gap-1 cursor-default', badgeSize, className)}
        >
          <Icon className={iconSize} />
          {showLabel && combined !== null && (
            <span>{(combined * 100).toFixed(0)}%</span>
          )}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Inline confidence indicator (icon only, no badge).
 */
export function ConfidenceIndicator({
  scores,
  className,
}: {
  scores?: ConfidenceScores;
  className?: string;
}) {
  const level = getConfidenceLevel(scores);
  const config = levelConfig[level];
  const Icon = config.icon;

  const colorClass = {
    high: 'text-green-600',
    medium: 'text-yellow-600',
    low: 'text-red-600',
    unknown: 'text-muted-foreground',
  }[level];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Icon className={cn('h-3.5 w-3.5', colorClass, className)} />
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{config.label}</p>
      </TooltipContent>
    </Tooltip>
  );
}
